/**
 * Zustand store for Reviews data
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const REVIEWS_KEY = "khays_reviews";

export interface ReviewMedia { id: string; url: string; type: "image" | "video"; }

export interface DeviceReview {
  id: string;
  deviceName: string;
  customerName: string;
  comment: string;
  media: ReviewMedia[];
  createdAt: number;
  enabled: boolean;
  stars?: number;
  /** True when the submitter has a Delivered/Completed order for this device. */
  verifiedBuyer?: boolean;
}

function loadFromStorage(): DeviceReview[] {
  try { const s = localStorage.getItem(REVIEWS_KEY); if (s) return JSON.parse(s); } catch {} return [];
}

interface ReviewState {
  reviews: DeviceReview[];
  _rev: number;
  setReviews: (reviews: DeviceReview[]) => void;
  reload: () => void;
}

export const useReviewStore = create<ReviewState>()(
  devtools(
    (set, get) => ({
      reviews: loadFromStorage(),
      _rev: 0,
      setReviews: (reviews) => {
        localStorage.setItem(REVIEWS_KEY, JSON.stringify(reviews));
        set({ reviews, _rev: get()._rev + 1 });
      },
      reload: () => set({ reviews: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "ReviewStore" }
  )
);

function save(reviews: DeviceReview[]): void { useReviewStore.getState().setReviews(reviews); }
function load(): DeviceReview[] { return useReviewStore.getState().reviews; }

export function getReviewsForDevice(deviceName: string): DeviceReview[] {
  return load().filter(r => r.deviceName === deviceName && r.enabled);
}

export function getAllReviews(): DeviceReview[] {
  return [...load()];
}

export function addReview(deviceName: string, customerName: string, comment: string, media: ReviewMedia[] = []): DeviceReview {
  const review: DeviceReview = {
    id: `rev_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    deviceName, customerName, comment, media, createdAt: Date.now(), enabled: true,
  };
  save([review, ...load()]);
  return review;
}

export function updateReview(id: string, updates: Partial<Pick<DeviceReview, "customerName" | "comment" | "enabled" | "stars">>): void {
  save(load().map(r => r.id === id ? { ...r, ...updates } : r));
}

export function addReviewMedia(reviewId: string, url: string, type: "image" | "video"): void {
  save(load().map(r => r.id === reviewId ? {
    ...r,
    media: [...r.media, { id: `rm_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`, url, type }],
  } : r));
}

export function removeReviewMedia(reviewId: string, mediaId: string): void {
  save(load().map(r => r.id === reviewId ? { ...r, media: r.media.filter(m => m.id !== mediaId) } : r));
}

export function removeReview(id: string): void {
  save(load().filter(r => r.id !== id));
}

export function moveReview(id: string, direction: "up" | "down"): void {
  const reviews = load();
  const idx = reviews.findIndex(r => r.id === id);
  if (idx < 0) return;
  const swapIdx = direction === "up" ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= reviews.length) return;
  const copy = [...reviews];
  [copy[idx], copy[swapIdx]] = [copy[swapIdx], copy[idx]];
  save(copy);
}

export function reorderReviews(orderedIds: string[]): void {
  const reviews = load();
  const map = new Map(reviews.map(r => [r.id, r]));
  const reordered = orderedIds.map(id => map.get(id)).filter(Boolean) as DeviceReview[];
  const remaining = reviews.filter(r => !orderedIds.includes(r.id));
  save([...reordered, ...remaining]);
}
